/**
 * Labelary API integration for ZPL preview generation
 * Based on: https://labelary.com/service.html
 */

export interface LabelaryOptions {
  dpmm: "6dpmm" | "8dpmm" | "12dpmm" | "24dpmm";
  width: number; // in inches
  height: number; // in inches
  index?: number; // label index (default: 0)
}

export interface LabelaryResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
  warnings?: string[];
}

/**
 * Convert DPI to DPMM (dots per millimeter)
 */
export function dpiToDpmm(dpi: number): LabelaryOptions["dpmm"] {
  // Standard conversions:
  // 203 DPI ≈ 8 DPMM
  // 300 DPI ≈ 12 DPMM
  // 600 DPI ≈ 24 DPMM
  // 152 DPI ≈ 6 DPMM
  if (dpi <= 152) return "6dpmm";
  if (dpi <= 203) return "8dpmm";
  if (dpi <= 300) return "12dpmm";
  return "24dpmm";
}

/**
 * Convert dots to inches based on DPI
 */
export function dotsToInches(dots: number, dpi: number): number {
  return Number((dots / dpi).toFixed(2));
}

/**
 * Generate a preview image using the Labelary API
 */
export async function generateLabelPreview(
  zpl: string,
  options: LabelaryOptions,
): Promise<LabelaryResponse> {
  try {
    const { dpmm, width, height, index = 0 } = options;

    // Construct the API URL
    const url = `https://api.labelary.com/v1/printers/${dpmm}/labels/${width}x${height}/${index}/`;

    // Make the POST request with ZPL in the body
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "image/png",
        "X-Linter": "On", // Enable ZPL linting for warnings
      },
      body: zpl,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      // Handle specific error cases
      if (response.status === 429) {
        errorMessage = "Rate limit exceeded. Please wait before trying again.";
      } else if (response.status === 413) {
        errorMessage =
          "Label is too large or complex for the Labelary service.";
      } else if (response.status === 400) {
        errorMessage = "Invalid ZPL or label dimensions.";
      }

      return {
        success: false,
        error: errorMessage,
      };
    }

    // Get warnings from response headers if available
    const warningsHeader = response.headers.get("X-Warnings");
    const warnings = warningsHeader ? parseWarnings(warningsHeader) : undefined;

    // Convert response to blob and create object URL
    const blob = await response.blob();
    const imageUrl = URL.createObjectURL(blob);

    return {
      success: true,
      imageUrl,
      warnings,
    };
  } catch (error) {
    console.error("Labelary API error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Parse warnings from the X-Warnings header
 */
function parseWarnings(warningsHeader: string): string[] {
  try {
    // Warnings are pipe-delimited with 5 attributes each
    // Format: byteIndex|byteSize|commandName|parameterNumber|message
    const warnings = warningsHeader.split("|");
    const parsedWarnings: string[] = [];

    // Process warnings in groups of 5
    for (let i = 0; i < warnings.length; i += 5) {
      if (i + 4 < warnings.length) {
        const message = warnings[i + 4];
        if (message) {
          parsedWarnings.push(message);
        }
      }
    }

    return parsedWarnings;
  } catch (error) {
    console.warn("Failed to parse warnings:", error);
    return [];
  }
}

/**
 * Clean up object URLs to prevent memory leaks
 */
export function cleanupImageUrl(imageUrl: string): void {
  if (imageUrl.startsWith("blob:")) {
    URL.revokeObjectURL(imageUrl);
  }
}
